import { useCallback, useRef, useState } from 'react'
import BottomSheet from './BottomSheet'
import { useI18n } from '../i18n'
import {
  bookingConsentFields,
  bookingConsentStep,
  canAskRecordingConsent,
} from '../utils/videoVisit'

/**
 * RecordingConsentSheet — MEET-patient PA0 (PROD-538, MEET_PLAN 3.7 / MEET_PLAN_UPDATE 2c).
 *
 * The recording question for a video visit: title, the consent text, two choices. The same text
 * is asked at booking (PA0, here) and before an in-app join (PA1, JoinVideoVisit).
 *
 * ★ The English consent text is a DRAFT until counsel answers (R-M2); the Spanish is a DRAFT
 *   for Dr. Rami (list 4). Until he approves it, the keys are PENDING_ES_WITHHELD
 *   (src/i18n/pendingEs.js): under es the question is never drawn and the visit is booked
 *   without a decision, which the API records as unrecorded (Q-M4).
 *
 * Props: open, busy (both buttons disabled), error (a sentence under the buttons),
 *   acceptLabel / declineLabel (already translated), onAccept, onDecline, onClose, children
 *   (drawn under the buttons).
 */
export default function RecordingConsentSheet({
  open,
  busy = false,
  error = null,
  acceptLabel,
  declineLabel,
  onAccept,
  onDecline,
  onClose,
  children = null,
}) {
  const { t } = useI18n()
  const NAVY = '#15354F', BLUE = '#46A8F9', SLATE = '#5F7A8E'
  return (
    <BottomSheet open={open} onClose={busy ? () => {} : onClose} zIndex={10000}>
      <div className="px-5 pt-2 pb-7" data-recording-consent-sheet="">
        <h3 className="text-[18px] font-extrabold" style={{ color: NAVY }}>
          {t('videoVisit.consentTitle')}
        </h3>
        <p className="mt-2 text-[13.5px] leading-relaxed" style={{ color: SLATE }}>
          {t('videoVisit.consentBody')}
        </p>
        <button
          type="button"
          data-consent-choice="accepted"
          onClick={onAccept}
          disabled={busy}
          className="mt-5 w-full rounded-xl py-3 text-[14.5px] font-bold text-white disabled:opacity-60"
          style={{ background: BLUE, boxShadow: '0 4px 12px rgba(70,168,249,0.3)' }}
        >
          {acceptLabel}
        </button>
        <button
          type="button"
          data-consent-choice="declined"
          onClick={onDecline}
          disabled={busy}
          className="mt-2.5 w-full rounded-xl border-[1.5px] py-3 text-[14.5px] font-bold disabled:opacity-60"
          style={{ borderColor: '#E2E8F0', color: NAVY, background: '#FFFFFF' }}
        >
          {declineLabel}
        </button>
        {error && (
          <p className="mt-3 text-[12.5px]" role="alert" style={{ color: '#b91c1c' }}>{error}</p>
        )}
        {children}
      </div>
    </BottomSheet>
  )
}

/**
 * PA0 — the booking-time question. `askRecordingConsent(visitType)` resolves, BEFORE the booking
 * is sent:
 *   { proceed: true, fields: {} }                          in person, or the question cannot be drawn
 *   { proceed: true, fields: { recordingConsent, consentVersion } }   the patient chose
 *   { proceed: false }                                     the patient closed the sheet: send nothing
 * `consentSheet` must be rendered by the screen that asks.
 */
export function useBookingRecordingConsent() {
  const { t, hasText } = useI18n()
  const [open, setOpen] = useState(false)
  const resolver = useRef(null)

  const askRecordingConsent = useCallback((visitType) => {
    if (bookingConsentStep(visitType, canAskRecordingConsent(hasText)) !== 'ask') {
      return Promise.resolve({ proceed: true, fields: {} })
    }
    return new Promise((resolve) => {
      resolver.current = resolve
      setOpen(true)
    })
  }, [hasText])

  const settle = (decision) => {
    const resolve = resolver.current
    resolver.current = null
    setOpen(false)
    if (!resolve) return
    resolve(decision ? { proceed: true, fields: bookingConsentFields(decision) } : { proceed: false })
  }

  const consentSheet = (
    <RecordingConsentSheet
      open={open}
      acceptLabel={t('videoVisit.bookingAgree')}
      declineLabel={t('videoVisit.bookingDecline')}
      onAccept={() => settle('accepted')}
      onDecline={() => settle('declined')}
      onClose={() => settle(null)}
    />
  )
  return { askRecordingConsent, consentSheet }
}
